import axiosIns from '../common/interceptor/axios-interceptor';
import logger from '../helpers/logger';

/**
 * Export data to Excel format via server-side generation
 * @param {Object} options
 * @param {Array}  options.data      Array of objects to export
 * @param {string} options.fileName  Base filename (without extension)
 * @param {string} options.sheetName Excel sheet name
 * @returns {Promise<Object>}        Result object with success flag and message
 */
export const exportToExcel = async ({ data, fileName, sheetName }) => {
  try {
    const response = await axiosIns.post(
      'export/excel',
      { fileName, sheetName, data, format: 'xlsx' },
      { responseType: 'blob' }
    );

    // Create blob and trigger download
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, message: 'Excel file downloaded successfully' };
  } catch (error) {
    logger.error('Excel export error:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to export Excel file',
      error,
    };
  }
};

/**
 * Export data to CSV format via server-side generation
 * @param {Object} options
 * @param {Array}  options.data     Array of objects to export
 * @param {string} options.fileName Base filename (without extension)
 * @returns {Promise<Object>}       Result object with success flag and message
 */
export const exportToCsv = async ({ data, fileName }) => {
  try {
    const response = await axiosIns.post(
      'export/csv',
      { fileName, data, format: 'csv' },
      { responseType: 'blob' }
    );

    // Create blob and trigger download
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, message: 'CSV file downloaded successfully' };
  } catch (error) {
    logger.error('CSV export error:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to export CSV file',
      error,
    };
  }
};

export const exportToJson = async ({ data, fileName }) => {
  try {
    const jsonString = JSON.stringify(data, null, 2);

    const blob = new Blob([jsonString], { type: 'application/json' });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.json`);

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, message: 'JSON file downloaded successfully' };
  } catch (error) {
    console.error('JSON export error:', error);
    return {
      success: false,
      message: 'Failed to export JSON file',
      error,
    };
  }
};

/**
 * Clean data for export by removing DataGrid-specific properties
 * and normalizing null/undefined to empty strings
 * @param {Array} data Array of objects
 * @returns {Array}     Cleaned array
 */
export const cleanDataForExport = (data) => {
  if (!Array.isArray(data)) return [];

  return data.map((row) => {
    const { id, ...cleanRow } = row;
    const normalizedRow = {};

    Object.entries(cleanRow).forEach(([key, value]) => {
      normalizedRow[key] = value == null ? '' : value;
    });

    return normalizedRow;
  });
};

/**
 * Legacy compatibility function - same interface as old xlsx implementation
 * @param {string}                prefix File prefix
 * @param {Array<Object>}         data   Data to export
 * @param {'excel'|'csv'} [format='excel']
 * @returns {Promise<Object>}
 */
export const legacyExport = async (prefix, data, format = 'excel') => {
  const cleanedData = cleanDataForExport(data);

  if (format === 'excel') {
    return await exportToExcel({
      data: cleanedData,
      fileName: prefix,
      sheetName: prefix,
    });
  } else {
    return await exportToCsv({
      data: cleanedData,
      fileName: prefix,
    });
  }
};
